import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

function navigateToHash(navigate: ReturnType<typeof useNavigate>, path: string) {
  const [pathname, hash] = path.split("#");
  navigate(pathname);
  if (hash) {
    // ページ遷移後にスクロール
    setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
  }
}

const navItems = [
  { label: "HOME", labelJa: "ホーム", path: "/", sub: null },
  {
    label: "SERVICE",
    labelJa: "サービス",
    path: "/service",
    sub: [
      { label: "ホームページ制作", path: "/service#svc-01" },
      { label: "SEO対策", path: "/service#svc-02" },
      { label: "MEO対策", path: "/service#svc-03" },
      { label: "LLMO対策", path: "/service#svc-04" },
      { label: "自社アプリ制作", path: "/service#svc-05" },
      { label: "リスティング広告", path: "/service#svc-06" },
      { label: "SNS広告・インフルエンサー", path: "/service#svc-07" },
    ],
  },
  { label: "STRENGTH", labelJa: "クラプリックスの強み", path: "/strength", sub: null },
  { label: "FLOW", labelJa: "ご相談の流れ", path: "/flow", sub: null },
  { label: "FAQ", labelJa: "よくある質問", path: "/faq", sub: null },
  { label: "CONTACT", labelJa: "お問い合わせ", path: "/contact", sub: null },
  { label: "COMPANY", labelJa: "会社情報", path: "/company", sub: null },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState<string | null>(null);
  const [subHovered, setSubHovered] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Desktop Top Header */}
      <header
        className="hidden lg:flex fixed left-0 top-0 right-0 h-20 items-center z-50 px-10"
        style={{
          background: "#ffffff",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {/* Top gold accent line */}
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #c00000, transparent)" }} />

        {/* Logo area */}
        <div className="flex-shrink-0 mr-8">
          <button onClick={() => navigate("/")} className="cursor-pointer focus:outline-none relative block">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at center, rgba(192,0,0,0.12) 0%, transparent 70%)", transform: "scale(2)" }} />
            <span style={{ fontFamily: "'Barlow', sans-serif", fontWeight: 700, fontSize: "24px", letterSpacing: "0.4em", color: "#111111", position: "relative" }}>
              CRUPLIX
            </span>
          </button>
        </div>

        {/* Nav items — center */}
        <nav className="flex items-center justify-center flex-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const isHov = hovered === item.path;
            const showSub = item.sub && isHov;
            return (
              <div
                key={item.path}
                className="relative"
                onMouseEnter={() => {
                  if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                  setHovered(item.path);
                }}
                onMouseLeave={() => {
                  hoverTimerRef.current = setTimeout(() => {
                    setHovered(null);
                    setSubHovered(null);
                  }, 200);
                }}
              >
                <button
                  onClick={() => navigate(item.path)}
                  className="relative flex items-center gap-1.5 cursor-pointer focus:outline-none transition-all duration-300"
                  style={{
                    padding: "14px 18px",
                    background: isHov || isActive ? "rgba(192,0,0,0.06)" : "transparent",
                    color: isActive || isHov ? "#c00000" : "#111111",
                  }}
                >
                  <span
                    className="font-bold whitespace-nowrap"
                    style={{
                      fontFamily: "'Barlow', sans-serif",
                      fontSize: "14px",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {item.label}
                  </span>
                  {item.sub && (
                    <span
                      className="flex items-center justify-center"
                      style={{
                        width: "18px",
                        height: "18px",
                        color: isHov || isActive ? "#c00000" : "#111111",
                        fontSize: "12px",
                        transition: "color 0.3s, transform 0.3s",
                        transform: showSub ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    >
                      <i className="ri-arrow-down-s-line" />
                    </span>
                  )}
                </button>

                {/* Sub menu — dropdown */}
                {item.sub && (
                  <div
                    className="absolute top-full left-0 flex flex-col"
                    onMouseEnter={() => {
                      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                    }}
                    onMouseLeave={() => {
                      hoverTimerRef.current = setTimeout(() => {
                        setHovered(null);
                        setSubHovered(null);
                      }, 200);
                    }}
                    style={{
                      marginTop: "0",
                      background: "#1a1a1a",
                      borderTop: "3px solid #c00000",
                      minWidth: "260px",
                      opacity: showSub ? 1 : 0,
                      transform: showSub ? "translateY(0)" : "translateY(-8px)",
                      pointerEvents: showSub ? "auto" : "none",
                      transition: "opacity 0.25s ease, transform 0.3s cubic-bezier(0.22,1,0.36,1)",
                      zIndex: 100,
                    }}
                  >
                    {item.sub.map((s) => {
                      const isSubHov = subHovered === s.label;
                      return (
                        <button
                          key={s.label}
                          onClick={() => navigateToHash(navigate, s.path)}
                          onMouseEnter={() => setSubHovered(s.label)}
                          onMouseLeave={() => setSubHovered(null)}
                          className="flex items-center gap-3 cursor-pointer w-full focus:outline-none transition-all duration-200"
                          style={{
                            padding: "14px 24px",
                            background: isSubHov ? "rgba(192,0,0,0.15)" : "transparent",
                            border: "none",
                            borderBottom: "1px solid rgba(255,255,255,0.06)",
                          }}
                        >
                          <span
                            style={{
                              fontFamily: "'Noto Sans JP', sans-serif",
                              fontSize: "13px",
                              letterSpacing: "0.05em",
                              color: isSubHov ? "#c00000" : "#ffffff",
                              transition: "color 0.2s",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {s.label}
                          </span>
                          <span className="ml-auto flex items-center justify-center" style={{ width: "16px", height: "16px", color: isSubHov ? "#c00000" : "rgba(255,255,255,0.4)", fontSize: "12px", transition: "color 0.2s" }}>
                            <i className="ri-arrow-right-line" />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Right area — CTA buttons */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-6">
          {/* 無料相談 */}
          <button
            onClick={() => navigate("/contact")}
            className="flex items-center gap-2 px-4 py-2.5 cursor-pointer whitespace-nowrap transition-all duration-300"
            style={{
              background: "#c00000",
              color: "#ffffff",
              fontFamily: "'Barlow', sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              letterSpacing: "0.06em",
              border: "none",
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.background = "#a00000";
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = "#c00000";
            }}
          >
            <span>無料相談</span>
            <span className="flex items-center justify-center" style={{ width: "14px", height: "14px" }}>
              <i className="ri-chat-1-line text-xs" />
            </span>
          </button>
        </div>

        {/* Bottom accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #c00000, transparent)", opacity: 0.3 }} />
      </header>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 z-50 flex items-center justify-between px-4"
        style={{ background: "rgba(255,255,255,0.97)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <button onClick={() => navigate("/")} className="cursor-pointer focus:outline-none">
          <span style={{ fontFamily: "'Barlow', sans-serif", fontWeight: 700, letterSpacing: "0.3em", fontSize: "18px", color: "#111111" }}>
            CRUPLIX
          </span>
        </button>
        <button
          className="w-8 h-8 flex items-center justify-center cursor-pointer focus:outline-none"
          style={{ color: "#111111" }}
          onClick={() => setMenuOpen(v => !v)}
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </header>

      {/* Mobile Menu */}
      <div
        className="lg:hidden fixed inset-0 z-40 flex flex-col transition-all duration-500"
        style={{
          background: "#ffffff",
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? "auto" : "none",
          transform: menuOpen ? "translateX(0)" : "translateX(-16px)",
        }}
      >
        {/* Top accent */}
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #c00000, transparent)" }} />

        {/* Logo row */}
        <div className="flex items-center justify-between px-6 h-14">
          <button onClick={() => navigate("/")} className="cursor-pointer focus:outline-none">
            <span style={{ fontFamily: "'Barlow', sans-serif", fontWeight: 700, fontSize: "20px", letterSpacing: "0.2em", color: "#111111" }}>CRUPLIX</span>
          </button>
          <button
            className="w-8 h-8 flex items-center justify-center cursor-pointer focus:outline-none"
            style={{ color: "#111111" }}
            onClick={() => setMenuOpen(false)}
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Divider */}
        <div style={{ height: "1px", background: "rgba(0,0,0,0.06)" }} />

        {/* Nav items */}
        <nav className="flex-1 flex flex-col overflow-y-auto pt-6 pb-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const isSubOpen = mobileSubOpen === item.path;
            return (
              <div key={item.path}>
                <div className="relative flex items-center" style={{ padding: "11px 32px" }}>
                  {/* Active left bar */}
                  {isActive && (
                    <span
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px]"
                      style={{ height: "40px", background: "#c00000", boxShadow: "0 0 10px rgba(192,0,0,0.8)" }}
                    />
                  )}

                  {/* Text block — tapping navigates */}
                  <button
                    onClick={() => { setMenuOpen(false); setMobileSubOpen(null); navigate(item.path); }}
                    className="flex flex-col flex-1 cursor-pointer focus:outline-none text-left"
                    style={{ background: "none", border: "none", padding: 0 }}
                  >
                    <span
                      style={{
                        fontFamily: "'Barlow', sans-serif",
                        fontSize: "18px",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        color: isActive || isSubOpen ? "#c00000" : "#111111",
                        lineHeight: 1,
                        marginBottom: "4px",
                      }}
                    >
                      {item.label}
                    </span>
                    <span
                      style={{
                        fontFamily: "'Noto Sans JP', sans-serif",
                        fontSize: "11px",
                        color: isActive || isSubOpen ? "rgba(192,0,0,0.7)" : "#111111",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {item.labelJa}
                    </span>
                  </button>

                  {/* Arrow — tapping toggles sub */}
                  {item.sub && (
                    <button
                      onClick={() => setMobileSubOpen(isSubOpen ? null : item.path)}
                      className="w-10 h-10 flex items-center justify-center cursor-pointer focus:outline-none shrink-0"
                      style={{ background: "none", border: "none", padding: 0 }}
                    >
                      <span
                        style={{
                          color: isSubOpen ? "#c00000" : "#111111",
                          fontSize: "20px",
                          transition: "transform 0.3s ease, color 0.3s ease",
                          transform: isSubOpen ? "rotate(90deg)" : "rotate(0deg)",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <i className="ri-arrow-right-s-line" />
                      </span>
                    </button>
                  )}
                </div>

                {/* Sub items — accordion */}
                {item.sub && (
                  <div
                    style={{
                      maxHeight: isSubOpen ? `${item.sub.length * 48}px` : "0px",
                      overflow: "hidden",
                      transition: "max-height 0.35s cubic-bezier(0.22,1,0.36,1)",
                      background: "rgba(0,0,0,0.02)",
                      borderLeft: isSubOpen ? "2px solid #c00000" : "2px solid transparent",
                      marginLeft: "32px",
                    }}
                  >
                    {item.sub.map((s, si) => (
                      <button
                        key={s.label}
                        onClick={() => { setMenuOpen(false); setMobileSubOpen(null); navigateToHash(navigate, s.path); }}
                        className="flex items-center gap-3 w-full cursor-pointer focus:outline-none"
                        style={{
                          padding: "12px 20px",
                          background: "transparent",
                          border: "none",
                          borderBottom: si < item.sub!.length - 1 ? "1px solid rgba(0,0,0,0.04)" : "none",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: "4px",
                            height: "4px",
                            background: "#c00000",
                            borderRadius: "50%",
                            opacity: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontFamily: "'Noto Sans JP', sans-serif",
                            fontSize: "12px",
                            color: "#111111",
                            letterSpacing: "0.04em",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {s.label}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                <div style={{ height: "1px", background: "rgba(0,0,0,0.06)", marginLeft: "32px" }} />
              </div>
            );
          })}
        </nav>

        {/* Bottom area — SNS + CTA */}
        <div className="px-8 pb-6 pt-4">
          <div style={{ height: "1px", background: "linear-gradient(90deg, rgba(192,0,0,0.3), transparent)", marginBottom: "14px" }} />

          {/* LINE link */}
          <a
            href="https://lin.ee/tP9rADP"
            target="_blank"
            rel="nofollow noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2.5 mb-3 cursor-pointer transition-all duration-300"
            style={{
              background: "transparent",
              color: "#c00000",
              fontFamily: "'Noto Sans JP', sans-serif",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "0.05em",
              textDecoration: "none",
              border: "1px solid #c00000",
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = "#c00000";
              (e.currentTarget as HTMLAnchorElement).style.color = "#0a0a0a";
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
              (e.currentTarget as HTMLAnchorElement).style.color = "#c00000";
            }}
          >
            <i className="ri-line-line text-lg" />
            公式LINE
          </a>

          <button
            onClick={() => navigate("/contact")}
            className="w-full py-3 text-sm cursor-pointer whitespace-nowrap font-bold tracking-widest"
            style={{ background: "#c00000", color: "#0a0a0a", fontFamily: "'Barlow', sans-serif" }}
          >
            無料相談
          </button>
        </div>

        {/* Bottom accent */}
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #c00000, transparent)", opacity: 0.3 }} />
      </div>
    </>
  );
}